import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { EpicPage, EpicHero, EpicCard, EpicTitle, Chip, EPIC } from "@/components/Epic";
import { StoryCard, STORIES } from "@/components/RoleplayStory";
import { toast } from "sonner";
import api, { reportApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Users, Loader2, Copy, Crown, Flame, Target, TrendingDown, LogOut, Plus, LogIn, Activity, Mail, Link2, Clock, Upload, Trash2, Image as ImageIcon, Share2, ExternalLink
} from "lucide-react";

const Agg = ({ icon: Icon, label, value, tone = "text-emerald-600" }) => (
  <EpicCard className="p-4" testId={`agg-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <Icon className={`w-4 h-4 ${tone}`} />
    <div className="text-2xl font-extrabold tracking-tight text-slate-900 font-display mt-2">{value}</div>
    <div className="text-[11px] text-slate-500">{label}</div>
  </EpicCard>
);

function relTime(iso) {
  if (!iso) return "Never practiced";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "Yesterday";
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function resizeToDataURL(file, max = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const hasAlpha = file.type === "image/png" || file.type === "image/webp";
        resolve(c.toDataURL(hasAlpha ? "image/png" : "image/jpeg", 0.9));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Teams() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState((params.get("join") || "").toUpperCase());
  const [busy, setBusy] = useState(false);
  const { user, refreshUser } = useAuth();

  const load = () => api.get("/teams/me").then((r) => setData(r.data)).catch(reportApiError("Teams"));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post("/teams", { name: name.trim() }); toast.success("Team created! Your Manager hub is now in the menu."); load(); refreshUser(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't create team"); } finally { setBusy(false); }
  };
  const join = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try { await api.post("/teams/join", { code: code.trim() }); toast.success("Joined team!"); load(); refreshUser(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't join"); } finally { setBusy(false); }
  };
  const leave = async () => {
    if (!window.confirm("Leave this team?")) return;
    try { await api.post("/teams/leave"); toast.success("Left team"); load(); refreshUser(); }
    catch { toast.error("Couldn't leave the team"); }
  };

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const logo = await resizeToDataURL(file);
      await api.put("/teams/logo", { logo });
      toast.success("Logo updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };
  const removeLogo = async () => {
    try { await api.delete("/teams/logo"); toast.success("Logo removed"); load(); }
    catch { toast.error("Couldn't remove the logo"); }
  };

  if (!data) return <div className="flex items-center justify-center py-32 text-emerald-600"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  if (!data.team) {
    return (
      <EpicPage testId="teams-empty">
        <EpicHero overline="Teams" title="Sell better," accent="together" subtitle="Create a team to coach your reps and see who needs help — or join your manager's team with a code." />
        <StoryCard story={STORIES.manager} cta="Create my team ↓" onCta={() => document.querySelector("[data-testid=team-name-input]")?.focus()} testId="manager-story" />
        <div className="grid md:grid-cols-2 gap-6">
          <EpicCard className="p-6">
            <EpicTitle icon={Plus}>Create a team</EpicTitle>
            <p className="text-sm text-slate-600 mt-1">You become the manager: invite reps, see the team table, run workshops.</p>
            <div className="flex gap-2 mt-4">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Team name (e.g. Alpha Closers)" className="bg-slate-50 border-slate-200 text-slate-900 h-11 rounded-xl" data-testid="team-name-input" />
              <Button onClick={create} disabled={busy} className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-[0_4px_14px_rgba(16,185,129,0.35)]" data-testid="create-team-btn">Create</Button>
            </div>
          </EpicCard>
          <EpicCard className="p-6">
            <EpicTitle icon={LogIn} tone="text-cyan-600">Join a team</EpicTitle>
            <p className="text-sm text-slate-600 mt-1">Got an invite code from your manager? Pop it in here.</p>
            <div className="flex gap-2 mt-4">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Team invite code" className="bg-slate-50 border-slate-200 text-slate-900 uppercase h-11 rounded-xl font-mono" data-testid="team-code-input" />
              <Button onClick={join} disabled={busy} className="h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold" data-testid="join-team-btn">Join</Button>
            </div>
          </EpicCard>
        </div>
      </EpicPage>
    );
  }

  const { team, is_manager, members, aggregate } = data;
  const inviteLink = `${window.location.origin}/app/teams?join=${team.code}`;
  const copyLink = () => { navigator.clipboard?.writeText(inviteLink); toast.success("Invite link copied"); };
  const emailInvite = () => {
    const subject = encodeURIComponent(`Join my sales team "${team.name}" on Pitch Craft`);
    const body = encodeURIComponent(`Hey,\n\nJoin my team on Pitch Craft and let's sharpen our sales skills together.\n\nJoin here: ${inviteLink}\nOr use invite code: ${team.code}\n\nSee you on the leaderboard!`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  return (
    <EpicPage testId="teams-page">
      <EpicHero overline={is_manager ? "Manager view" : "My team"} title={team.name}
        subtitle={is_manager ? "Your squad at a glance — who's on fire, who needs a nudge, and what they're sharing." : "Your team's table. Climb it by putting the reps in."}
        aside={(
          <div className="flex items-center gap-3">
            {team.logo_url && <img src={team.logo_url} alt={team.name} className="w-16 h-16 rounded-2xl object-contain bg-white border border-slate-200 p-1 shadow-sm" data-testid="team-logo" />}
            <Button onClick={leave} variant="outline" className="h-10 rounded-full border-slate-300 text-slate-600 hover:text-red-600 hover:border-red-300 hover:bg-red-50" data-testid="leave-team-btn"><LogOut className="w-4 h-4 mr-1" /> Leave</Button>
          </div>
        )}>
        <button onClick={() => { navigator.clipboard?.writeText(team.code); toast.success("Code copied"); }} data-testid="team-code-copy"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800">
          <Copy className="w-3.5 h-3.5" /> Invite code <span className="font-mono text-emerald-300">{team.code}</span>
        </button>
        <Chip><Users className="w-3.5 h-3.5 text-emerald-600" /> {aggregate.members} members</Chip>
        <Chip><Target className="w-3.5 h-3.5 text-cyan-600" /> {aggregate.avg_win_rate}% avg win</Chip>
      </EpicHero>

      {is_manager && (
        <div className="grid lg:grid-cols-2 gap-6">
          <EpicCard className="p-6" testId="team-branding-card">
            <EpicTitle icon={ImageIcon}>Company logo</EpicTitle>
            <p className="text-sm text-slate-600 mt-1">Brand the workspace for your firm. Square images look best.</p>
            <div className="flex items-center gap-3 mt-4">
              <div className="w-16 h-16 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {team.logo_url ? <img src={team.logo_url} alt="" className="w-full h-full object-contain p-1" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
              </div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" data-testid="logo-upload-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                <span className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-[0_4px_14px_rgba(16,185,129,0.3)]" data-testid="logo-upload-btn">
                  {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {team.logo_url ? "Change logo" : "Upload logo"}
                </span>
              </label>
              {team.logo_url && <Button onClick={removeLogo} variant="ghost" className="text-slate-500 hover:text-red-600" data-testid="logo-remove-btn"><Trash2 className="w-4 h-4 mr-1" /> Remove</Button>}
            </div>
          </EpicCard>
          <EpicCard className="p-6" testId="invite-reps-card">
            <EpicTitle icon={Mail} tone="text-cyan-600">Invite your reps</EpicTitle>
            <p className="text-sm text-slate-600 mt-1">Share the link or email an invite. Anyone with it can join your team.</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Input value={inviteLink} readOnly className="flex-1 min-w-[200px] bg-slate-50 border-slate-200 text-slate-600 text-xs font-mono h-10 rounded-xl" data-testid="invite-link-input" />
              <Button onClick={copyLink} variant="outline" className="h-10 rounded-xl border-slate-300 bg-white text-slate-900 hover:bg-slate-50" data-testid="copy-invite-btn"><Link2 className="w-4 h-4 mr-1" /> Copy link</Button>
              <Button onClick={emailInvite} className="h-10 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold" data-testid="email-invite-btn"><Mail className="w-4 h-4 mr-1" /> Email invite</Button>
            </div>
          </EpicCard>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Agg icon={Users} label="Members" value={aggregate.members} />
        <Agg icon={Target} label="Avg win rate" value={`${aggregate.avg_win_rate}%`} tone="text-cyan-600" />
        <Agg icon={Activity} label="Team conversations" value={aggregate.total_conversations} tone="text-slate-500" />
        <Agg icon={Flame} label="Avg streak" value={aggregate.avg_streak} tone="text-amber-500" />
      </div>

      <EpicCard className="overflow-hidden" testId="team-table">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" /> Team table</h2>
          <span className="text-xs text-slate-500">Ranked by win rate</span>
        </div>
        {members.map((m, i) => (
          <motion.div key={m.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.03 }}
            className={`flex items-center gap-4 px-5 py-3.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors ${m.id === user?.id ? "bg-emerald-50/70" : ""}`} data-testid={`team-member-${m.id}`}>
            <div className="w-8 flex justify-center">{i === 0 ? <Crown className="w-5 h-5 text-amber-500" /> : <span className="text-sm font-mono font-bold text-slate-500">{i + 1}</span>}</div>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-black font-bold shrink-0 ring-2 ring-white shadow">{m.name?.[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 truncate">{m.name} {m.id === team.owner_id && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 ml-1 font-bold">MANAGER</span>}</div>
              <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5 flex-wrap">
                <span className="flex items-center gap-1"><Flame className="w-3 h-3 text-amber-500" />{m.streak}</span>
                <span>{m.conversations} calls</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{relTime(m.last_practiced_at)}</span>
                {is_manager && m.weakest && <span className="flex items-center gap-1 text-red-600"><TrendingDown className="w-3 h-3" />{m.weakest}</span>}
              </div>
            </div>
            <div className="hidden sm:block w-24"><div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${m.win_rate}%` }} /></div></div>
            <div className="text-right">
              <div className="font-extrabold text-slate-900 font-display tracking-tight">{m.win_rate}%</div>
              <div className="text-[10px] text-slate-500 uppercase font-mono">win rate</div>
            </div>
          </motion.div>
        ))}
      </EpicCard>

      <SharedFeed isManager={is_manager} />
    </EpicPage>
  );
}

function SharedFeed({ isManager }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/teams/shared").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  return (
    <div className={`${EPIC} overflow-hidden`} data-testid="team-shared-feed">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Share2 className="w-4 h-4 text-emerald-600" /> Shared conversations</div>
        <span className="text-xs text-slate-500">{isManager ? "Review your reps' calls in one place" : "Calls your team has shared"}</span>
      </div>
      {items === null ? (
        <div className="p-6 text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-6 text-sm text-slate-500" data-testid="team-shared-empty">
          Nothing shared yet. After any roleplay, hit <span className="font-semibold text-slate-700">Share conversation</span> on the analysis page and it'll appear here for the whole team.
        </div>
      ) : items.map((s, i) => (
        <motion.div key={s.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
          className="flex items-center gap-4 px-5 py-4 border-b border-slate-200 last:border-0" data-testid={`shared-item-${s.share_id}`}>
          <img src={s.persona_avatar} alt="" className="w-10 h-10 rounded-full object-cover object-top border border-slate-200 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 truncate">
              {s.rep_name} <span className="text-slate-400 font-normal">vs</span> {s.persona_name}
              {s.mine && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 ml-2">YOU</span>}
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5 flex-wrap">
              <span>{s.persona_title}</span>
              {s.channel_label && <span>· {s.channel_label}</span>}
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{relTime(s.shared_at)}</span>
            </div>
            {s.summary && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{s.summary}</p>}
          </div>
          <div className="text-right shrink-0">
            <div className={`font-bold font-mono ${s.won ? "text-emerald-600" : "text-slate-700"}`}>{s.rating ?? "–"}</div>
            <div className="text-[10px] text-slate-600 uppercase">{s.won ? "deal won" : "score"}</div>
          </div>
          <a href={`/share/${s.share_id}`} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline" data-testid={`shared-open-${s.share_id}`}>
            Review <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </motion.div>
      ))}
    </div>
  );
}
